"use client";

import { useTranslations } from "next-intl";
import { BADGE_NEUTRAL } from "@/lib/badge-styles";

const PLACEMENT = {
  /** Above the tag, left edges aligned. */
  above: "bottom-full left-0 mb-5",
  /** Below the tag, right edges aligned (a tag in a card's right-hand column). */
  belowEnd: "top-full right-0 mt-5",
  /**
   * Above the tag, right edges aligned: it grows back over the card, so a tag
   * near a card's left edge inside a clipping card is not cut off.
   */
  aboveEnd: "bottom-full right-0 mb-5",
} as const;

/**
 * The "Affiliate link" tag, with the commission line in a tooltip on hover
 * (owner, 2026-09-15: it used to be a line on every card).
 *
 * The tooltip is CSS-only and stays in the DOM at opacity 0, so a screen
 * reader still reads the commission line with the card. Inside a card that is
 * itself a link the tag must NOT be focusable (an interactive element inside
 * a link is invalid), so `focusable` is for tags outside one, like the chat
 * row, where keyboard focus then shows the tooltip too. On a touch screen
 * there is no hover: the visible tag is the disclosure there.
 */
export default function AffiliateBadge({
  placement = "above",
  focusable = false,
  plain = false,
  className = "",
}: {
  placement?: keyof typeof PLACEMENT;
  focusable?: boolean;
  /** The label as bare text in the caller's type (the grid card footer), not a pill. */
  plain?: boolean;
  className?: string;
}) {
  const t = useTranslations("affiliates");
  return (
    <span
      tabIndex={focusable ? 0 : undefined}
      className={`group/aff relative inline-flex focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gold ${plain ? "min-w-0" : "shrink-0 rounded-md"} ${className}`}
    >
      {/* The tooltip text is a child, so it is read with the tag: no aria-describedby, or it is announced twice. */}
      <span className={plain ? "block truncate" : BADGE_NEUTRAL}>{t("label")}</span>
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-dropdown whitespace-nowrap rounded-md bg-navy px-8 py-5 text-left font-sans text-2xs font-medium normal-case leading-snug tracking-normal text-white opacity-0 shadow-e3 transition-opacity duration-150 group-hover/aff:opacity-100 group-focus-visible/aff:opacity-100 ${PLACEMENT[placement]}`}
      >
        {t("disclosure")}
      </span>
    </span>
  );
}
